import { getGlobalLogger } from '@zg-rentals/logger-base';
import type { LogHttpRequest } from '@zg-rentals/monitor-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { makeModuleInstance } from '@zg-rentals/util';
import { Maybe, result, type Result, toolbelt, Unit } from 'true-myth';

type ErrorContext = Record<string, unknown>;
const errorContext = makeModuleInstance<ErrorContext>({});
export const MAX_STACK_TRACE_LINES = 15;
export const MAX_STACK_TRACE_LENGTH = 1_500;
export const ERROR_BOUNDARY_PREFIX = 'ErrorBoundary';

type LogErrorOptions = {
  error: Error | unknown;
  context?: string | Record<string, unknown>;
  errorType?: string;
  httpRequest?: LogHttpRequest;
};

type PreparedLogError = {
  error: Error | unknown;
  errorType: string;
  fullContext: ErrorContext;
  httpRequest?: LogHttpRequest;
  message: string;
  prefixedErrorType: string;
  totalErrorName: string;
};

/**
 * Adds global error context that will be logged with all other error information to Splunk.
 * Local context passed to `logError` will take precedence and be spread _after_ the global context
 */
export const setGlobalErrorContext = (ctx: ErrorContext) => errorContext.set({ ...errorContext.get(), ...ctx });

const enabled = makeModuleInstance(true);

/**
 * An escape hatch to disable log error, perhaps if you intend to redirect logged
 * out users and have a bunch of pending promises/network requests that will be
 * terminated by redirecting.
 */
export const disableLogError = () => enabled.set(false);

export type { LogHttpRequest };

/**
 * Throw this error to attach context to the error that can be consumed higher up
 * in the stack. In general this error type should be used only in module code to
 * propagate error context out to apps that call `logError`.
 */
export class ContextError extends Error {
  constructor(
    name: string,
    public readonly context: Record<string, unknown>,
  ) {
    super(name);
    this.name = name;
  }

  public static is(error: unknown): error is ContextError {
    return error instanceof ContextError;
  }
}

function truncateStackTrace(stack: Maybe<string>) {
  return stack.map((contents) => {
    let splitLines = contents.split('\n');
    if (splitLines.length > MAX_STACK_TRACE_LINES) {
      splitLines = splitLines.slice(0, MAX_STACK_TRACE_LINES).concat('...');
    }

    const newStack = splitLines.join('\n');
    return newStack.length > MAX_STACK_TRACE_LENGTH ? newStack.slice(0, MAX_STACK_TRACE_LENGTH) + '...' : newStack;
  });
}

const getEnvironmentContext = () => {
  const isServer = typeof window === 'undefined';

  return {
    errorPrefix: `${isServer ? 'httpServer' : 'browser'} error`,
    fullContext: isServer
      ? {}
      : {
          originPath: window.location.origin + window.location.pathname,
          userAgent: window.navigator.userAgent,
        },
  };
};

const prepareLogError = ({
  error,
  context,
  errorType = error instanceof Error ? error.name : 'UnknownError',
  httpRequest,
}: LogErrorOptions): Result<PreparedLogError, string> =>
  result.tryOrElse(
    (reason) => `prepareLogError: ${reason}`,
    () => {
      const isErrorInstance = error instanceof Error;
      const { errorPrefix, fullContext: environmentContext } = getEnvironmentContext();

      const localContext = typeof context === 'string' ? { context } : (context ?? {});
      const contextErrorContext = ContextError.is(error) ? error.context : {};

      // Truncate stack trace so Splunk logs don't lose formatting
      const stack = truncateStackTrace(Maybe.of((isErrorInstance ? error : new Error()).stack)).unwrapOr(
        '[no stack trace provided]',
      );

      if (isErrorInstance) {
        // Ensure monitor?.error doesn't receive lengthy/circular stack traces.
        error.stack = stack;
      }

      const fullContext = {
        stack,
        ...errorContext.get(),
        ...contextErrorContext,
        ...localContext,
        ...environmentContext,
      };

      // Serialize error into a readable string, useful for Splunk logs.
      const message = isErrorInstance ? error.message : `Serialized error: ${JSON.stringify(error)}`;
      errorType = errorType.replace(/\s/g, '');

      return {
        error,
        errorType,
        fullContext,
        httpRequest,
        message,
        prefixedErrorType: `${errorPrefix} type ${errorType}`,
        totalErrorName: `${errorPrefix} total`,
      };
    },
  );

const reportToSplunk = ({ fullContext, message, prefixedErrorType }: PreparedLogError): Result<Unit, string> =>
  result
    .tryOrElse(
      (reason) => `getGlobalLogger(${reason})`,
      () => Maybe.of(getGlobalLogger(prefixedErrorType)),
    )
    .andThen((logger) => toolbelt.fromMaybe('reportToSplunk: logger is undefined', logger))
    .andThen((logger) =>
      result.tryOrElse(
        (reason) => `logger.error(${reason})`,
        () => logger.error(fullContext, `log-error reporting: ${message}`),
      ),
    )
    .map(() => Unit)
    .mapErr((reason) => `reportToSplunk: ${reason}`);

const reportToMarlin = ({ prefixedErrorType, totalErrorName }: PreparedLogError): Result<Unit, string> =>
  result
    .tryOrElse(
      (reason) => `reportToMarlin: ${reason}`,
      () => {
        const monitor = getGlobalMonitor();
        monitor.count({ name: totalErrorName });
        monitor.count({ name: prefixedErrorType });
      },
    )
    .map(() => Unit);

const reportToDragnet = ({ error, errorType, fullContext, httpRequest }: PreparedLogError): Result<Unit, string> =>
  result
    .tryOrElse(
      (reason) => `reportToDragnet: ${reason}`,
      () => {
        if (error instanceof Error) {
          getGlobalMonitor().error({
            error,
            httpRequest,
            isErrorBoundary: errorType.startsWith(ERROR_BOUNDARY_PREFIX),
            errorType,
            context: fullContext,
          });
        }
      },
    )
    .map(() => Unit);

/**
 * Call this function to log errors in the Rental Platform standardized way. Assuming
 * your app is configured with a Bootstrap instance, your errors should show up in some
 * combination of Splunk, Marlin, and Dragnet (based on bootstrap configuration).
 */
export function logError({
  error,
  context,
  errorType = error instanceof Error ? error.name : 'UnknownError',
  httpRequest,
}: LogErrorOptions): Result<Unit, Array<string>> {
  if (!enabled.get()) {
    return result.ok(Unit);
  }

  return prepareLogError({ error, context, errorType, httpRequest })
    .mapErr((reason) => [reason])
    .andThen((prepared) =>
      result
        .transposeAll([reportToSplunk(prepared), reportToMarlin(prepared), reportToDragnet(prepared)])
        .map(() => Unit),
    )
    .inspectErr(
      // eslint-disable-next-line no-console
      (reasons) => console.error(`logError failed`, { reasons }),
    );
}
